<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ProductBlock from '../parts/ProductBlock.vue';
import InfoGrid from '../parts/InfoGrid.vue';
import { useCountdown } from '../parts/useCountdown';
import { formatDateTime, formatPrice, hasValue } from '../helpers';

const props = defineProps({
  source: { type: Object, required: true },
  platform: { type: String, default: '' },
});

const { t, locale } = useI18n();

const ret = computed(() => props.source.return || {});
const product = computed(() => props.source.product || {});

const STATUS_CLASSES = {
  new: 'bg-n-blue-3 text-n-blue-11',
  approved: 'bg-n-teal-3 text-n-teal-11',
  rejected: 'bg-n-ruby-3 text-n-ruby-11',
  awaiting_item: 'bg-n-amber-3 text-n-amber-11',
  refunded: 'bg-n-teal-3 text-n-teal-11',
};

const statusClass = computed(
  () => STATUS_CLASSES[ret.value.status] || 'bg-n-alpha-2 text-n-slate-11'
);

const statusLabel = computed(() => {
  const key = `MULTICHAT.SOURCE.RETURN.STATUSES.${String(ret.value.status || '').toUpperCase()}`;
  return ret.value.status && t(key) !== key ? t(key) : ret.value.status;
});

const deadline = computed(() => ret.value.deadline);
const { parts, label: remaining, isExpired } = useCountdown(deadline);

const isDecided = computed(() =>
  ['approved', 'rejected', 'refunded'].includes(ret.value.status)
);

const deadlineTone = computed(() => {
  if (isExpired.value) return 'bg-n-ruby-2 border-n-ruby-5 text-n-ruby-11';
  if (parts.value && parts.value.totalHours < 24) {
    return 'bg-n-ruby-2 border-n-ruby-4 text-n-ruby-11';
  }
  return 'bg-n-amber-2 border-n-amber-4 text-n-amber-11';
});

const details = computed(() => [
  {
    key: 'order',
    label: t('MULTICHAT.SOURCE.ORDER'),
    value: ret.value.order_id,
    mono: true,
  },
  {
    key: 'amount',
    label: t('MULTICHAT.SOURCE.RETURN.AMOUNT'),
    value: hasValue(ret.value.amount)
      ? formatPrice(ret.value.amount, locale.value)
      : '',
  },
  {
    key: 'items',
    label: t('MULTICHAT.SOURCE.RETURN.ITEMS'),
    value: ret.value.items,
  },
  {
    key: 'created',
    label: t('MULTICHAT.SOURCE.RETURN.CREATED'),
    value: formatDateTime(ret.value.created_at, locale.value),
  },
  {
    key: 'pvz',
    label: t('MULTICHAT.SOURCE.RETURN.PICKUP_POINT'),
    value: ret.value.pickup_point,
    wide: true,
  },
]);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center gap-2">
      <span class="text-[13px] font-semibold text-n-slate-12">
        {{ t('MULTICHAT.SOURCE.RETURN.NUMBER') }}
        <span class="font-mono">{{ ret.id }}</span>
      </span>
      <span
        v-if="ret.status"
        class="inline-flex items-center h-5 px-2 rounded-full text-[11px] font-semibold"
        :class="statusClass"
      >
        {{ statusLabel }}
      </span>
      <span
        v-if="ret.photos"
        class="inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-n-alpha-2 text-n-slate-11 text-[11px] font-medium"
      >
        <span class="i-lucide-image size-3" />
        {{ t('MULTICHAT.SOURCE.PHOTOS', { count: ret.photos }) }}
      </span>
    </div>
    <div
      v-if="deadline && !isDecided"
      class="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs"
      :class="deadlineTone"
    >
      <span class="i-lucide-timer size-4 flex-shrink-0" />
      <span class="font-medium">
        {{
          t('MULTICHAT.SOURCE.RETURN.DEADLINE', {
            date: formatDateTime(deadline, locale),
          })
        }}
      </span>
      <span class="flex-1" />
      <span class="font-semibold tabular-nums">
        {{
          isExpired
            ? t('MULTICHAT.SOURCE.RETURN.OVERDUE')
            : t('MULTICHAT.SOURCE.COUNTDOWN.LEFT', { time: remaining })
        }}
      </span>
    </div>
    <div
      v-if="ret.reason"
      class="flex flex-col gap-0.5 p-3 rounded-lg bg-n-alpha-1 border border-n-weak"
    >
      <span
        class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
      >
        {{ t('MULTICHAT.SOURCE.RETURN.REASON') }}
      </span>
      <p class="m-0 text-[13px] leading-5 text-n-slate-12 whitespace-pre-line">
        {{ ret.reason }}
      </p>
    </div>
    <ProductBlock v-if="product.name" :product="product" :platform="platform" />
    <InfoGrid :items="details" />
  </div>
</template>
